import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert, CircularProgress, LinearProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { smartCardAPI, BACKEND_BASE_URL } from '../services/api';

export default function SmartCardGenerate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    gemstone_name: '', weight: '', color: '', shape_and_cut: '', species: '',
    cut: '', origin: '', treatment: '', metal: '', quality_grade: '',
    finish: '', comments: '', product_title: '',
    serial_number: '', signature_authority: '',
  });
  const [gemstoneImage, setGemstoneImage] = useState(null);
  const [labLogo, setLabLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const pollRef = useRef(null);

  const pollCard = useCallback(async (cardId) => {
    try {
      const res = await smartCardAPI.detail(cardId);
      const card = res.data;
      if (card.assets_status === 'ready') {
        setPreview(card);
        setGenerating(false);
        toast.success('Smart card generated successfully!');
        return true;
      }
      if (card.assets_status === 'failed') {
        setGenerating(false);
        setGenerateError(card.assets_error || 'Asset generation failed. Please try again.');
        toast.error('Smart card generation failed.');
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileChange = (setter) => (e) => {
    if (e.target.files[0]) setter(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gemstone_name) {
      setError('Gemstone name is required');
      return;
    }
    setError('');
    setGenerateError('');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (gemstoneImage) fd.append('gemstone_image', gemstoneImage);
      if (labLogo) fd.append('lab_logo', labLogo);
      const res = await smartCardAPI.generate(fd);
      const card = res.data;

      if (card.assets_status === 'ready') {
        setPreview(card);
        toast.success('Smart card generated successfully!');
      } else {
        setGenerating(true);
        setPreview(card);
        let attempts = 0;
        pollRef.current = setInterval(async () => {
          attempts++;
          const done = await pollCard(card.id);
          if (done || attempts >= 60) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (attempts >= 60 && !done) {
              setGenerating(false);
              setGenerateError('Generation timed out. Please refresh the cards list.');
            }
          }
        }, 3000);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const msgs = Object.values(data).flat().join(', ');
        setError(msgs || 'Generation failed');
      } else {
        setError('Generation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Generate Smart Card
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create a premium PVC-style gemstone identification card
      </Typography>

      <Paper sx={{ p: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#1a237e' }}>
            Gemstone Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Gemstone Name" name="gemstone_name"
                value={form.gemstone_name} onChange={handleChange} required placeholder="e.g. Blue Sapphire" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Weight (Carat)" name="weight"
                value={form.weight} onChange={handleChange} placeholder="e.g. 3.25 ct" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Color" name="color"
                value={form.color} onChange={handleChange} placeholder="e.g. Cornflower Blue" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Shape & Cut" name="shape_and_cut"
                value={form.shape_and_cut} onChange={handleChange} placeholder="e.g. Oval Brilliant" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Species" name="species"
                value={form.species} onChange={handleChange} placeholder="e.g. Natural Diamond" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Cut" name="cut"
                value={form.cut} onChange={handleChange} placeholder="e.g. Excellent" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Origin" name="origin"
                value={form.origin} onChange={handleChange} placeholder="e.g. Sri Lanka" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Treatment" name="treatment"
                value={form.treatment} onChange={handleChange} placeholder="e.g. Heat Treated" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Metal" name="metal"
                value={form.metal} onChange={handleChange} placeholder="e.g. 18K White Gold" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Quality Grade" name="quality_grade"
                value={form.quality_grade} onChange={handleChange} placeholder="e.g. AAA / Premium" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Finish" name="finish"
                value={form.finish} onChange={handleChange} placeholder="e.g. Excellent" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Product Title" name="product_title"
                value={form.product_title} onChange={handleChange} placeholder="e.g. Blue Diamond Ring" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Serial Number" name="serial_number"
                value={form.serial_number} onChange={handleChange} placeholder="Auto if blank" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Comments" name="comments"
                value={form.comments} onChange={handleChange} multiline rows={2} placeholder="Additional remarks" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Signature Authority" name="signature_authority"
                value={form.signature_authority} onChange={handleChange} placeholder="e.g. Dr. Rajesh Kumar, Laboratory Director" />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#1a237e' }}>
            Uploads
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth sx={{ textTransform: 'none', py: 1.5 }}>
                {gemstoneImage ? gemstoneImage.name : 'Upload Gemstone Image'}
                <input type="file" hidden accept="image/*" onChange={handleFileChange(setGemstoneImage)} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth sx={{ textTransform: 'none', py: 1.5 }}>
                {labLogo ? labLogo.name : 'Upload Laboratory Logo'}
                <input type="file" hidden accept="image/*" onChange={handleFileChange(setLabLogo)} />
              </Button>
            </Grid>
          </Grid>

          <Button type="submit" variant="contained" size="large" disabled={loading || generating}
            sx={{ py: 1.3, bgcolor: '#1a237e', '&:hover': { bgcolor: '#283593' } }}>
            {loading ? <CircularProgress size={24} /> : 'Generate Smart Card'}
          </Button>
        </form>
      </Paper>

      {generating && (
        <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Generating Smart Card...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creating QR code, front image, back image, and print-quality PDF. This may take a few moments.
          </Typography>
          <LinearProgress sx={{ borderRadius: 1 }} />
          {preview && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Card: {preview.card_id}
            </Typography>
          )}
        </Paper>
      )}

      {generateError && (
        <Alert severity="error" sx={{ mb: 3 }}>{generateError}</Alert>
      )}

      {preview && !generating && preview.front_image && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Card Preview — {preview.card_id}
          </Typography>
          <Grid container spacing={3}>
            {preview.front_image && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Front</Typography>
                <Box component="img"
                  src={preview.front_image.startsWith('http') ? preview.front_image : `${BACKEND_BASE_URL}${preview.front_image}`}
                  alt="Front"
                  sx={{ width: '100%', maxWidth: 400, height: 'auto', aspectRatio: '856/540', borderRadius: 2, border: '1px solid #ddd' }} />
              </Grid>
            )}
            {preview.back_image && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Back</Typography>
                <Box component="img"
                  src={preview.back_image.startsWith('http') ? preview.back_image : `${BACKEND_BASE_URL}${preview.back_image}`}
                  alt="Back"
                  sx={{ width: '100%', maxWidth: 400, height: 'auto', aspectRatio: '856/540', borderRadius: 2, border: '1px solid #ddd' }} />
              </Grid>
            )}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => navigate('/admin/smart-cards')}>
              View All Cards
            </Button>
            <Button variant="outlined"
              onClick={() => window.open(`${BACKEND_BASE_URL}/api/smart-cards/${preview.id}/preview/?side=front&watermark=true`, '_blank')}>
              HTML Preview (Front)
            </Button>
            <Button variant="outlined"
              onClick={() => window.open(`${BACKEND_BASE_URL}/api/smart-cards/${preview.id}/preview/?side=back`, '_blank')}>
              HTML Preview (Back)
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
